import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import GUIDES from '../data/pageGuides.json';

const SITE = 'https://moneyfeed.io';

const withGuideFaqs = (jsonLd, path) => {
  const g = GUIDES[path];
  if (!g) return jsonLd;
  const items = g.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } }));
  const arr = Array.isArray(jsonLd) ? [...jsonLd] : (jsonLd ? [jsonLd] : []);
  const i = arr.findIndex((o) => o && o['@type'] === 'FAQPage');
  if (i >= 0) arr[i] = { ...arr[i], mainEntity: [...arr[i].mainEntity, ...items] };
  else arr.push({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: items });
  return arr;
};

// Removes duplicate <link rel=canonical> and duplicate JSON-LD blocks that appear when
// react-helmet re-adds tags already injected by the build-time prerender / static <head>.
// CRITICAL: it must ONLY remove the static prerender nodes (tagged data-prerender in index.js
// before Helmet mounts) and NEVER a react-helmet-managed node — removing a Helmet node makes
// Helmet's later reconciliation on route change call removeChild on a detached node and throw
// "Cannot read properties of null (reading 'removeChild')", aborting the destination render.
const isPrerender = (el) => el.hasAttribute('data-prerender');

const metaKey = (m) => (m.getAttribute('name') ? `n:${m.getAttribute('name')}` : `p:${m.getAttribute('property')}`);

const dedupeHead = () => {
  const metas = Array.from(document.querySelectorAll('meta[name="description"], meta[property^="og:"], meta[name^="twitter:"]'));
  const helmetKeys = new Set(metas.filter((m) => !isPrerender(m)).map(metaKey));
  metas.forEach((m) => { if (isPrerender(m) && helmetKeys.has(metaKey(m))) m.remove(); });

  const cans = Array.from(document.querySelectorAll('link[rel="canonical"]'));
  if (cans.length > 1) {
    const helmetCanonical = cans.find((c) => !isPrerender(c));
    if (helmetCanonical) {
      cans.forEach((c) => { if (c !== helmetCanonical && isPrerender(c)) c.remove(); });
    } else {
      cans.slice(1).forEach((c) => { if (isPrerender(c)) c.remove(); });
    }
  }

  const scripts = Array.from(document.querySelectorAll('script[type="application/ld+json"]'));
  const seen = new Set();
  const collect = (s) => {
    try {
      const j = JSON.parse(s.textContent);
      (Array.isArray(j) ? j : [j]).forEach((o) => o && o['@type'] && seen.add(o['@type']));
    } catch (e) { /* ignore */ }
  };
  // Register @types owned by react-helmet (non-prerender) nodes first — these are authoritative.
  scripts.filter((s) => !isPrerender(s)).forEach(collect);
  // Remove ONLY static prerender scripts whose @types are already covered; keep unique static
  // ones (e.g. site-wide Organization / WebSite / SoftwareApplication that Helmet doesn't inject).
  scripts.filter(isPrerender).forEach((s) => {
    let types = [];
    try {
      const j = JSON.parse(s.textContent);
      types = (Array.isArray(j) ? j : [j]).map((o) => o && o['@type']).filter(Boolean);
    } catch (e) {
      return;
    }
    if (types.length && types.every((t) => seen.has(t))) {
      s.remove();
    } else {
      types.forEach((t) => seen.add(t));
    }
  });
};

export const Seo = ({ title, description, path = '', image, jsonLd: rawJsonLd, alternates, lang, dir, noindex, robots }) => {
  const url = `${SITE}${path}`;
  const jsonLd = withGuideFaqs(rawJsonLd, path);

  useEffect(() => {
    const t1 = setTimeout(dedupeHead, 80);
    const t2 = setTimeout(dedupeHead, 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [url]);

  const img = image || `${SITE}/og-image.png`;
  return (
    <Helmet {...(lang ? { htmlAttributes: { lang, dir: dir || 'ltr' } } : {})}>
      <title>{title}</title>
      <meta name="description" content={description} />
      {robots ? <meta name="robots" content={robots} /> : noindex ? <meta name="robots" content="noindex,follow" /> : null}
      {!noindex && <link rel="canonical" href={url} />}
      {alternates && alternates.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
      ))}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export default Seo;
